import React, { useEffect } from "react";
import dayjs from "dayjs";
import { padStart } from "lodash";
import stylesV2 from "./IdleModalContent.module.scss";
import stylesV3 from "./IdleModalContentV2.module.scss";
import { useCurrentPatient } from "features/patientDashboard/hooks/useCurrentPatient";
import { Button, ButtonRole } from "components/Button";
import { useSchedulingABVariant } from "features/scheduling/utils/useSchedulingABVariant";

export interface IdleModalContentProps {
  timer: number;
  dismiss: () => void;
}

export const IdleModalContent: React.FC<IdleModalContentProps> = ({
  timer,
  dismiss,
}) => {
  const { isV3 } = useSchedulingABVariant();
  const styles = isV3 ? stylesV3 : stylesV2;
  const [endTime] = React.useState(dayjs().add(timer, "millisecond"));
  const [currentTime, setCurrentTime] = React.useState(dayjs());
  const [timedOut, setTimedOut] = React.useState(false);
  const { logout, handleLogoutRedirect } = useCurrentPatient();

  // Calculate remaining time in seconds
  const secondsLeft = Math.max(0, endTime.diff(currentTime, "second"));

  useEffect(() => {
    if (secondsLeft === 0 && !timedOut) {
      setTimedOut(true);
      handleLogoutRedirect();
    }
    if (timedOut) return;
    const countdownTimer = window.setInterval(() => {
      setCurrentTime(dayjs());
    }, 1000);
    return () => {
      window.clearInterval(countdownTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft, timedOut]);

  const countdown = () => {
    const min = Math.floor(secondsLeft / 60);
    const sec = secondsLeft % 60;
    return `${min}:${padStart(sec.toString(), 2, "0")}`;
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.header}>Session Timeout Due to Inactivity</h3>
      <div className={styles.body}>
        <div>Your session is about to expire.</div>
        <div>You will be logged out in {countdown()} minutes.</div>
      </div>
      <div className={styles.buttonRow}>
        <Button
          className={styles.button}
          onClick={timedOut ? undefined : handleLogoutRedirect}
          role={ButtonRole.Ghost}
          size="large"
          text="Log Out"
          disabled={timedOut}
        />
        <Button
          className={styles.button}
          onClick={timedOut ? undefined : dismiss}
          size="large"
          text="Continue"
          disabled={timedOut}
        />
      </div>
    </div>
  );
};
